import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";
import { LanguageSelector } from "@/components/layout/language-selector";
import { LOGO } from "@/constants/assets";
import "@/styles/template-home.css";

const HEADER_LOGO = "/template-home/assets/header-logo.png";

// Header nav — mirrors the blueberry-sensei page so both feel like one cinematic shell.
// HOME is marked active (red + gradient underline) on this page.
const getNavItems = (t) => [
  { label: t("nav.home"), to: "/", active: true },
  { label: t("nav.center"), to: "/center" },
  { label: t("nav.aboutBlueberry"), to: "/blueberry-sensei" },
  { label: t("nav.contact"), to: "/contact" },
];

const DEFAULT_TWEAKS = {
  glow: 1,
  tilt: 1,
  vignette: 0.55,
  showBg: true,
  showPanels: true,
  monochrome: false,
};

const CALENDAR_DAYS = [
  { className: "d off", value: "27" },
  { className: "d off", value: "28" },
  { className: "d off", value: "29" },
  { className: "d off", value: "30" },
  { className: "d", value: "1" },
  { className: "d has-event", value: "2" },
  { className: "d", value: "3" },
  { className: "d", value: "4" },
  { className: "d", value: "5" },
  { className: "d has-event", value: "6" },
  { className: "d", value: "7" },
  { className: "d", value: "8" },
  { className: "d has-event", value: "9" },
  { className: "d", value: "10" },
  { className: "d", value: "11" },
  { className: "d", value: "12" },
  { className: "d", value: "13" },
  { className: "d", value: "14" },
  { className: "d", value: "15" },
  { className: "d today", value: "16" },
  { className: "d", value: "17" },
  { className: "d", value: "18" },
  { className: "d has-event", value: "19" },
  { className: "d", value: "20" },
  { className: "d", value: "21" },
];

const BAR_HEIGHTS = ["30%", "52%", "40%", "76%", "48%", "62%", "92%", "38%", "54%", "70%", "44%", "58%"];

// Typing-text quotes per locale. Vietnamese is the source of truth — translate from `vi`,
// never from `en`. Falls back to `vi` for any language not listed here.
const QUOTES_BY_LANG = {
  vi: [
    "Kẻ thật sự kiên cường không phải người chưa từng gục ngã, mà là người đã bị số phận nghiền nát vô số lần… nhưng vẫn chọn dịu dàng với thế giới.",
    "Đau đớn nhất không phải là mất đi ai đó… mà là sống tiếp quá lâu để rồi từng chút một quên mất giọng nói, nụ cười và hơi ấm của người mình từng xem là cả thế giới.",
    "Có những người mang số phận như cơn mưa lạnh, sợ làm ướt cuộc đời người khác nên cuối cùng chỉ biết lặng lẽ thu mình giữa nhân gian rộng lớn.",
    "Cô độc tuyệt đối không phải là đứng một mình giữa bóng tối… mà là khi xung quanh tràn ngập ánh lửa và tiếng cười, còn trái tim mình lại lạnh đến mức chẳng thể chạm vào bất kỳ hơi ấm nào.",
    "Nếu thế giới này quá tàn khốc, cậu sẽ tự tay tạo nên một thế giới dịu dàng hơn. Nếu quá khứ chỉ toàn đổ nát, cậu sẽ dùng phần đời còn lại để viết nên một tương lai khác.",
  ],
  en: [
    "The truly resilient one is not the person who has never fallen, but the one who has been crushed by fate countless times… and yet still chooses to be gentle to the world.",
    "The deepest pain is not losing someone… it is living on for too long, until little by little you forget the voice, the smile, and the warmth of the person you once called your whole world.",
    "There are those whose fate is like a cold rain — afraid to wet the lives of others, in the end they can only quietly fold themselves away in this vast human world.",
    "Absolute loneliness is not standing alone in the dark… it is when the world around you overflows with firelight and laughter, yet your own heart is so cold it cannot reach any warmth at all.",
    "If this world is too cruel, you will build a gentler world with your own hands. If the past is nothing but ruins, you will use the rest of your life to write a different future.",
  ],
  ja: [
    "本当に強い人とは、一度も倒れたことのない人ではなく、運命に幾度となく打ち砕かれてなお、世界に優しくあろうと選ぶ人のこと。",
    "最も深い痛みは、誰かを失うことではない——生き続ける時間が長すぎて、かつて世界そのものだった人の声も、笑顔も、ぬくもりも、少しずつ忘れてしまうこと。",
    "冷たい雨のような運命を背負う者がいる。誰かの人生を濡らすことを恐れて、結局この広い人の世にひっそりと身を縮めるしかない。",
    "本当の孤独とは、闇の中にひとり立つことではない——周りが灯と笑い声に満ちていても、自分の心がどんなぬくもりにも触れられないほど冷え切っていること。",
    "もしこの世界があまりに残酷ならば、君は自らの手で、より優しい世界を作るだろう。もし過去が瓦礫ばかりならば、君は残された人生で、もう一つの未来を書き上げるだろう。",
  ],
  zh: [
    "真正坚强的人，不是从未跌倒过的人，而是被命运碾碎无数次……却依然选择温柔以待这个世界的人。",
    "最深的痛苦，不是失去某个人……而是活得太久，渐渐忘记了那个曾被自己视为整个世界的人的声音、笑容与温度。",
    "有些人，命如冷雨。怕沾湿别人的人生，最终只能在这茫茫人间，悄悄把自己收起来。",
    "真正的孤独，不是独自站在黑暗里……而是身边火光与笑声四溢，自己的心却冷得，触不到任何一丝温暖。",
    "若这世界太过残酷，你便亲手造出一个更温柔的世界。若过往满是废墟，你便用余生，写出另一种未来。",
  ],
  ko: [
    "진정으로 강한 사람은 한 번도 쓰러져 본 적 없는 사람이 아니라, 운명에 수없이 짓밟히고도… 여전히 세상에 다정하기를 선택하는 사람이다.",
    "가장 깊은 아픔은 누군가를 잃는 것이 아니다… 너무 오래 살아남아, 한때 온 세상이었던 사람의 목소리와 미소와 온기를 조금씩 잊어가는 것이다.",
    "차가운 비 같은 운명을 짊어진 사람이 있다. 누군가의 삶을 적실까 두려워, 결국 이 넓은 세상 속에 조용히 자신을 접어두는 수밖에 없다.",
    "절대적인 외로움은 어둠 속에 홀로 서 있는 것이 아니다… 주위가 불빛과 웃음으로 가득한데, 내 마음만 그 어떤 온기에도 닿지 못할 만큼 차가워진 그 순간이다.",
    "이 세상이 너무 잔혹하다면, 너는 네 손으로 더 다정한 세상을 만들 것이다. 과거가 폐허뿐이라면, 너는 남은 생으로 또 다른 미래를 써 내려갈 것이다.",
  ],
};

const COPY = {
  vi: {
    pageTitle: "PHUONG — Người bạn AI lặng lẽ ở phía bên kia",
    nav: {
      about: "About Blueberry Sensei",
      policy: "Policy",
      contact: "Contact",
      language: "Language",
    },
    todo: {
      tag: "THÁNG 5 · 2026",
      calendar: "CN T2 T3 T4 T5 T6 T7",
      reminder: "Nhắc ngủ sớm",
      stats: ["Task", "Routine", "Habit"],
    },
    social: {
      tag: "FOR YOU · @linh",
      handle: '@linh_hehe · "về với em ♡"',
      likes: "23.5K",
      comments: "1.2K",
      badge: "9+ MỚI",
    },
    money: {
      tag: "VÍ CHÍNH · VND",
      available: "Số dư khả dụng",
      spend: "Chi tiêu tháng này",
      availableValue: "52,250,000",
      spendValue: "−6,850,000",
      unit: "VND",
    },
    quotes: [
      "Kẻ thật sự kiên cường không phải người chưa từng gục ngã, mà là người đã bị số phận nghiền nát vô số lần… nhưng vẫn chọn dịu dàng với thế giới.",
      "Đau đớn nhất không phải là mất đi ai đó… mà là sống tiếp quá lâu để rồi từng chút một quên mất giọng nói, nụ cười và hơi ấm của người mình từng xem là cả thế giới.",
      "Có những người mang số phận như cơn mưa lạnh, sợ làm ướt cuộc đời người khác nên cuối cùng chỉ biết lặng lẽ thu mình giữa nhân gian rộng lớn.",
      "Cô độc tuyệt đối không phải là đứng một mình giữa bóng tối… mà là khi xung quanh tràn ngập ánh lửa và tiếng cười, còn trái tim mình lại lạnh đến mức chẳng thể chạm vào bất kỳ hơi ấm nào.",
      "Nếu thế giới này quá tàn khốc, cậu sẽ tự tay tạo nên một thế giới dịu dàng hơn. Nếu quá khứ chỉ toàn đổ nát, cậu sẽ dùng phần đời còn lại để viết nên một tương lai khác.",
    ],
    quoteAuthor: "— Blueberry Sensei",
    dock: {
      updates: "Cập nhật & sửa chữa",
      campaigns: "Sự kiện & quà tặng",
      tiktok: "TikTok",
      facebook: "Facebook",
      download: "Tải xuống",
    },
    tweaks: {
      title: "TWEAKS",
      glow: "Glow strength",
      tilt: "3D Tilt",
      vignette: "Vignette",
      showBg: "Show hero background",
      showPanels: "Show floating panels",
      monochrome: "Monochrome (mood mode)",
    },
    loading: "Đang tải...",
  },
  en: {
    pageTitle: "PHUONG — The quiet AI companion on the other side",
    nav: {
      about: "About Blueberry Sensei",
      policy: "Policy",
      contact: "Contact",
      language: "Language",
    },
    todo: {
      tag: "MAY · 2026",
      calendar: "SU MO TU WE TH FR SA",
      reminder: "Sleep early reminder",
      stats: ["Task", "Routine", "Habit"],
    },
    social: {
      tag: "FOR YOU · @linh",
      handle: '@linh_hehe · "come back to me ♡"',
      likes: "23.5K",
      comments: "1.2K",
      badge: "9+ NEW",
    },
    money: {
      tag: "PRIMARY WALLET · VND",
      available: "Available balance",
      spend: "This month spend",
      availableValue: "52,250,000",
      spendValue: "−6,850,000",
      unit: "VND",
    },
    quotes: [
      "Even when the road ahead is dark and blurred, I still keep walking forward alone…",
      "Building an entire world alone — just because I do not want anyone else to feel lonely like me.",
      "Long night, cold coffee, code still running. I am still here.",
      "People only cherish someone when they can no longer meet them again.",
      "Every line of code, every pixel, carries an untold story.",
      "Youth and unfinished dreams — I left everything behind me.",
    ],
    quoteAuthor: "— Blueberry Sensei",
    dock: {
      updates: "Updates & fixes",
      campaigns: "Events & gifts",
      tiktok: "TikTok",
      facebook: "Facebook",
      download: "Download",
    },
    tweaks: {
      title: "TWEAKS",
      glow: "Glow strength",
      tilt: "3D Tilt",
      vignette: "Vignette",
      showBg: "Show hero background",
      showPanels: "Show floating panels",
      monochrome: "Monochrome (mood mode)",
    },
    loading: "Loading...",
  },
};

const postToParent = (message) => {
  try {
    window.parent.postMessage(message, "*");
  } catch {
    // no-op
  }
};

const HomePage = () => {
  const { lang, t } = useLanguage();
  const preloadVideoRef = useRef(null);
  // COPY only ships vi/en — fall back to vi for any other language code.
  const activeLang = lang === "en" ? "en" : "vi";
  const copy = COPY[activeLang];
  // Typing-text quotes are translated for all 5 supported langs; fall back to vi.
  const quotes = QUOTES_BY_LANG[lang] ?? QUOTES_BY_LANG.vi;
  const navItems = getNavItems(t);

  const [stageScale, setStageScale] = useState(1);
  const [typedQuote, setTypedQuote] = useState("");
  const [activeQuote, setActiveQuote] = useState("");
  const [tweaks, setTweaks] = useState(DEFAULT_TWEAKS);
  const [showTweakPanel, setShowTweakPanel] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    const body = document.body;

    document.title = copy.pageTitle;
    body.classList.add("landing-template-body");

    return () => {
      document.title = previousTitle;
      body.classList.remove("landing-template-body");
    };
  }, [copy.pageTitle]);

  useEffect(() => {
    const fitStage = () => {
      const sx = window.innerWidth / 1920;
      const sy = window.innerHeight / 1080;
      const compactViewport = window.innerWidth < 1024;

      if (!compactViewport) {
        setStageScale(Math.min(sx, sy));
        return;
      }

      // Mobile/tablet: boost stage so overlays (quote/dock/panels) stay readable.
      const phoneViewport = window.innerWidth < 640;
      const boostFactor = phoneViewport ? 3.1 : 2.2;
      const boostedScale = Math.min(Math.min(sx, sy) * boostFactor, sy * 0.94);
      setStageScale(boostedScale);
    };

    fitStage();
    window.addEventListener("resize", fitStage);

    return () => window.removeEventListener("resize", fitStage);
  }, []);

  useEffect(() => {
    const syncMenuState = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    syncMenuState();
    window.addEventListener("resize", syncMenuState);

    return () => window.removeEventListener("resize", syncMenuState);
  }, []);

  useEffect(() => {
    const video = preloadVideoRef.current;
    if (!video) {
      return undefined;
    }

    let cancelled = false;

    const markReady = () => {
      if (!cancelled) {
        setVideoReady(true);
      }
    };

    const markError = () => {
      if (!cancelled) {
        setVideoReady(true);
      }
    };

    video.addEventListener("canplaythrough", markReady);
    video.addEventListener("error", markError);

    if (video.readyState >= 4) {
      markReady();
    } else {
      video.load();
    }

    return () => {
      cancelled = true;
      video.removeEventListener("canplaythrough", markReady);
      video.removeEventListener("error", markError);
    };
  }, []);

  useEffect(() => {
    if (!videoReady) {
      setTypedQuote("");
      return undefined;
    }

    let cancelled = false;
    let quoteIndex = 0;

    const sleep = (ms) => new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

    const run = async () => {
      while (!cancelled) {
        const text = quotes[quoteIndex % quotes.length];
        setActiveQuote(text);

        for (let index = 1; index <= text.length && !cancelled; index += 1) {
          setTypedQuote(text.slice(0, index));
          await sleep(42 + Math.random() * 30);
        }

        await sleep(2800);

        for (let index = text.length; index >= 0 && !cancelled; index -= 1) {
          setTypedQuote(text.slice(0, index));
          await sleep(22);
        }

        await sleep(600);
        quoteIndex += 1;
      }
    };

    setTypedQuote("");
    setActiveQuote(quotes[0] ?? "");
    run();

    return () => {
      cancelled = true;
    };
  }, [quotes, videoReady]);

  useEffect(() => {
    const handleMessage = (event) => {
      const message = event.data;
      if (!message || typeof message !== "object") {
        return;
      }

      if (message.type === "__activate_edit_mode") {
        setShowTweakPanel(true);
      }

      if (message.type === "__deactivate_edit_mode") {
        setShowTweakPanel(false);
      }
    };

    window.addEventListener("message", handleMessage);
    postToParent({ type: "__edit_mode_available" });

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const updateTweak = (key, value) => {
    setTweaks((previous) => {
      const next = { ...previous, [key]: value };
      postToParent({ type: "__edit_mode_set_keys", edits: { [key]: value } });
      return next;
    });
  };

  if (!videoReady) {
    return (
      <div className="landing-template-root">
        <video
          ref={preloadVideoRef}
          src="https://assets.phuongai.com/0525.mp4"
          preload="auto"
          muted
          playsInline
          style={{
            position: "fixed",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
            inset: 0,
          }}
        />

        <div className="min-h-screen bg-[#030014] flex items-center justify-center relative overflow-hidden">
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF2E63]/10 rounded-full blur-[120px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 4, repeat: Infinity }}
          />

          <motion.div
            className="relative z-10 flex flex-col items-center gap-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.img
              src={LOGO}
              alt="Phuong"
              className="w-20 h-20 object-contain"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            />

            <div className="relative w-12 h-12">
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#FF2E63]"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
              />
              <div className="absolute inset-0 rounded-full border-2 border-white/5" />
            </div>

            <p className="text-white/40 text-sm tracking-[0.3em] uppercase">
              {copy.loading}
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-template-root">
      {/* Header — pulled OUT of .stage-frame so it lives at real viewport scale
          (not scaled with the 1920px stage canvas). This makes it visually identical
          to /blueberry-sensei when switching tabs. */}
      <header className="fixed inset-x-0 top-0 z-50">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-4 sm:gap-4 sm:px-5 md:px-8 md:py-5 lg:grid-cols-[1fr_auto_1fr] lg:gap-6 lg:px-16 lg:py-6">
          <Link
            to="/"
            className="justify-self-start"
            data-testid="logo-link"
          >
            <img
              src={HEADER_LOGO}
              alt="Phuong"
              className="h-9 w-9 object-contain lg:h-10 lg:w-10"
            />
          </Link>

          <nav className="hidden items-center gap-10 lg:flex" aria-label="Primary">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`cinematic-nav-link font-cinzel text-[11px] font-semibold uppercase transition-colors ${
                  item.active
                    ? "cinematic-red cinematic-nav-active"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 justify-self-end">
            <LanguageSelector
              variant="cinematic"
              iconOnly
              className="justify-self-end"
            />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-[#090718]/75 text-white/80 backdrop-blur-md transition hover:border-[#FF2E63]/70 hover:text-[#FF2E63] lg:hidden"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.button
              type="button"
              aria-label="Close navigation menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 z-[70] bg-[#02010d]/70 backdrop-blur-[2px] lg:hidden"
            />

            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-[80] w-[min(84vw,320px)] border-l border-[#FF2E63]/35 bg-[#060313]/96 p-5 shadow-[-24px_0_70px_rgba(0,0,0,0.58)] lg:hidden"
            >
              <div className="flex items-center justify-between">
                <span className="font-cinzel text-[11px] font-semibold tracking-[0.24em] text-white/70">
                  MENU
                </span>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close navigation menu"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white/80 transition hover:border-[#FF2E63]/70 hover:text-[#FF2E63]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <nav className="mt-8 flex flex-col gap-2">
                {navItems.map((item) => (
                  <Link
                    key={`drawer-${item.to}`}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`rounded-xl border px-4 py-3 text-sm uppercase tracking-[0.16em] transition ${
                      item.active
                        ? "border-[#FF2E63]/65 bg-[#FF2E63]/15 text-[#ffd5e2]"
                        : "border-white/10 bg-white/0 text-white/75 hover:border-[#FF2E63]/45 hover:text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <motion.div
        className="stage-frame"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      >
        {tweaks.showBg ? (
          <div className="stage-frame-video" aria-hidden="true">
            <video
              className="stage-frame-video-media"
              src="https://assets.phuongai.com/0525.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
            />
          </div>
        ) : null}

        <div
          className={`stage${tweaks.monochrome ? " stage-monochrome" : ""}`}
          id="stage"
          data-screen-label="01 Landing"
          style={{
            "--glow-strength": tweaks.glow,
            "--tilt": tweaks.tilt,
            "--vignette": tweaks.vignette,
          }}
        >
          {tweaks.showBg ? <div className="bg-hero"></div> : null}
          <div className="bg-grid"></div>
          <div className="scanline"></div>
          <div
            className="stage-layout"
            style={{
              transform: `translate(-50%, -50%) scale(${stageScale})`,
            }}
          >

          <div className="panel-stage stage-phuntodo">
            <section
              className="panel panel-phuntodo float-a"
              aria-label="PHUNTODO miniapp"
              style={{ display: tweaks.showPanels ? undefined : "none" }}
            >
              <span className="near-edge" aria-hidden="true"></span>
              <div className="panel-head">
                <div className="panel-title">
                  <span className="pt-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2.5" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                      <circle cx="8" cy="15" r="1.2" fill="currentColor" />
                      <circle cx="12" cy="15" r="1.2" fill="currentColor" />
                      <circle cx="16" cy="15" r="1.2" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="pt-name">
                    PHUN<b>TODO</b>
                  </span>
                </div>
                <span className="head-tag">{copy.todo.tag}</span>
              </div>

              <div className="todo-body">
                <div className="todo-col">
                  <div className="cal">
                    <div className="cal-month">{copy.todo.calendar}</div>
                    <div className="cal-grid">
                      {CALENDAR_DAYS.map((day) => (
                        <span key={`${day.className}-${day.value}`} className={day.className}>
                          {day.value}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="todo-row">
                    <span className="dot"></span>
                    <div>
                      <div className="ti-title">{copy.todo.reminder}</div>
                      <div className="ti-time">10:00 PM</div>
                    </div>
                    <div className="ti-spacer"></div>
                    <div className="ti-check" aria-label="checkbox"></div>
                  </div>
                </div>

                <div className="todo-col side">
                  <div className="todo-stats">
                    <div className="stat-block">
                      <div className="stat-num">12</div>
                      <div className="stat-lbl">{copy.todo.stats[0]}</div>
                    </div>
                    <div className="stat-block">
                      <div className="stat-num">5</div>
                      <div className="stat-lbl">{copy.todo.stats[1]}</div>
                    </div>
                    <div className="stat-block">
                      <div className="stat-num">3</div>
                      <div className="stat-lbl">{copy.todo.stats[2]}</div>
                    </div>
                  </div>
                  <div className="progress-ring">
                    <svg viewBox="0 0 100 100">
                      <defs>
                        <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ff8fb0" />
                          <stop offset="100%" stopColor="#881a74" />
                        </linearGradient>
                      </defs>
                      <circle className="ring-bg" cx="50" cy="50" r="42" fill="none" strokeWidth="8" />
                      <circle
                        className="ring-fg"
                        cx="50"
                        cy="50"
                        r="42"
                        fill="none"
                        strokeWidth="8"
                        strokeDasharray="264"
                        strokeDashoffset="63"
                      />
                    </svg>
                    <div className="ring-label">76%</div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="panel-stage stage-phunsocial">
            <section
              className="panel panel-phunsocial float-b"
              aria-label="PHUNSOCIAL miniapp"
              style={{ display: tweaks.showPanels ? undefined : "none" }}
            >
              <span className="near-edge" aria-hidden="true"></span>
              <div className="panel-head">
                <div className="panel-title">
                  <span className="pt-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 7h16v10a2 2 0 0 1-2 2H8l-4 3V7Z" />
                      <circle cx="9" cy="12" r="1.4" fill="currentColor" />
                      <circle cx="15" cy="12" r="1.4" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="pt-name">
                    PHUN<b>SOCIAL</b>
                  </span>
                </div>
                <span className="head-tag">{copy.social.tag}</span>
              </div>

              <div className="social-feed">
                <img
                  src="/template-home/assets/phunsocial-feed.png"
                  alt="Feed cover"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
                <div className="feed-shade"></div>
                <div className="feed-handle">{copy.social.handle}</div>
              </div>

              <div className="social-stats">
                <span className="stat">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9Z" />
                  </svg>
                  {copy.social.likes}
                </span>
                <span className="stat">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12Z" />
                  </svg>
                  {copy.social.comments}
                </span>
                <span className="badge">{copy.social.badge}</span>
              </div>
            </section>
          </div>

          <div className="panel-stage stage-phunmoney">
            <section
              className="panel panel-phunmoney float-c"
              aria-label="PHUNMONEY miniapp"
              style={{ display: tweaks.showPanels ? undefined : "none" }}
            >
              <span className="near-edge" aria-hidden="true"></span>
              <div className="panel-head">
                <div className="panel-title">
                  <span className="pt-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 7h15a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7Z" />
                      <path d="M3 7V5.5A1.5 1.5 0 0 1 4.5 4H17" />
                      <circle cx="17" cy="13.5" r="1.4" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="pt-name">
                    PHUN<b>MONEY</b>
                  </span>
                </div>
                <span className="head-tag">{copy.money.tag}</span>
              </div>

              <div className="money-body">
                <div className="money-stack">
                  <div>
                    <div className="label">{copy.money.available}</div>
                    <div className="value">
                      {copy.money.availableValue}
                      <span className="unit">{copy.money.unit}</span>
                    </div>
                  </div>
                  <div>
                    <div className="label">{copy.money.spend}</div>
                    <div className="value neg">
                      {copy.money.spendValue}
                      <span className="unit">{copy.money.unit}</span>
                    </div>
                  </div>
                </div>

                <div className="donut" aria-hidden="true">
                  <svg viewBox="0 0 100 100">
                    <defs>
                      <linearGradient id="donutA" x1="0" x2="1">
                        <stop offset="0%" stopColor="#ff5a8c" />
                        <stop offset="100%" stopColor="#881a74" />
                      </linearGradient>
                      <linearGradient id="donutB" x1="0" x2="1">
                        <stop offset="0%" stopColor="#307ED7" />
                        <stop offset="100%" stopColor="#134379" />
                      </linearGradient>
                      <linearGradient id="donutC" x1="0" x2="1">
                        <stop offset="0%" stopColor="#FFDC64" />
                        <stop offset="100%" stopColor="#C67645" />
                      </linearGradient>
                    </defs>
                    <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="14" />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="url(#donutA)"
                      strokeWidth="14"
                      strokeDasharray="125 251"
                      strokeDashoffset="0"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="url(#donutB)"
                      strokeWidth="14"
                      strokeDasharray="75 251"
                      strokeDashoffset="-125"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="url(#donutC)"
                      strokeWidth="14"
                      strokeDasharray="50 251"
                      strokeDashoffset="-200"
                    />
                  </svg>
                </div>

                <div className="bars" aria-hidden="true">
                  {BAR_HEIGHTS.map((height) => (
                    <div key={height} className="bar" style={{ height }}></div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <aside className="quote" id="about">
            <span className="q-mark">“</span>
            <p className="q-text" id="quote-text">
              <span className="q-sizer" aria-hidden="true">{activeQuote || " "}</span>
              <span className="q-typed">
                {typedQuote}
                <span className="q-cursor">|</span>
              </span>
            </p>
            <span className="q-mark close">”</span>
            <div className="q-author">{copy.quoteAuthor}</div>
          </aside>

          <div className="dock" id="contact">
            <div className="dock-line"></div>
            <div className="dock-inner">
              <div className="dock-icons left">
                <a href="https://phuongai.com/updates" target="_blank" rel="noopener noreferrer" className="dock-icon" title={copy.dock.updates}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-3.2-6.9" />
                    <path d="M21 4v5h-5" />
                  </svg>
                </a>
                <a href="https://phuongai.com/campaigns" target="_blank" rel="noopener noreferrer" className="dock-icon" title={copy.dock.campaigns}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="9" width="18" height="11" rx="2" />
                    <path d="M3 13h18" />
                    <path d="M12 9v11" />
                    <path d="M8.5 9c-1.5 0-2.5-1-2.5-2.3S7 4.5 8.5 4.5c2 0 3.5 4.5 3.5 4.5s-2 0-3.5 0Z" />
                    <path d="M15.5 9c1.5 0 2.5-1 2.5-2.3S17 4.5 15.5 4.5c-2 0-3.5 4.5-3.5 4.5s2 0 3.5 0Z" />
                  </svg>
                </a>
              </div>

              <div className="dock-icons right">
                <a
                  href="https://www.tiktok.com/@phuong_ai_official"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dock-icon"
                  title={copy.dock.tiktok}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19.6 6.4a5.5 5.5 0 0 1-3.7-1.6 5.5 5.5 0 0 1-1.4-3.3h-3v13a2.6 2.6 0 1 1-2.6-2.6c.28 0 .54.04.78.12V8.9a5.7 5.7 0 1 0 4.82 5.6V9.34a8.5 8.5 0 0 0 5.1 1.65V7.94c-.01 0-.01-1.54-.01-1.54Z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61576498730894"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dock-icon"
                  title={copy.dock.facebook}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.5-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.89h-2.33v6.99A10 10 0 0 0 22 12Z" />
                  </svg>
                </a>
              </div>
            </div>

            <a
              className="dock-orb pulse"
              href="https://phuongai.com/download"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={copy.dock.download}
              title={copy.dock.download}
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="orb-mark"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M5 21h14" />
              </svg>
            </a>
          </div>

          <div id="tweaks-root"></div>
          <div id="policy" style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}></div>
          </div>
        </div>
      </motion.div>

      <div
        id="tweaks-panel"
        style={{
          position: "fixed",
          right: 18,
          bottom: 18,
          zIndex: 9999,
          width: 280,
          padding: "16px 18px",
          background: "rgba(8, 12, 30, 0.92)",
          backdropFilter: "blur(14px)",
          border: "1px solid rgba(255, 90, 140, 0.35)",
          borderRadius: 14,
          boxShadow: "0 0 24px rgba(255, 58, 130, 0.25), 0 10px 40px rgba(0,0,0,0.5)",
          color: "#E5FFFF",
          fontFamily: "\"RedRose\", system-ui, sans-serif",
          fontSize: 13,
          display: showTweakPanel ? "block" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, letterSpacing: "0.12em", fontSize: 13 }}>{copy.tweaks.title}</div>
          <button
            id="tw-close"
            type="button"
            onClick={() => {
              setShowTweakPanel(false);
              postToParent({ type: "__edit_mode_dismissed" });
            }}
            style={{ background: "none", border: "none", color: "#ff5a8c", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="tw-glow"
            style={{ display: "flex", justifyContent: "space-between", fontSize: 11, letterSpacing: "0.06em", color: "rgba(229,255,255,0.7)", marginBottom: 4 }}
          >
            <span>{copy.tweaks.glow}</span>
            <span>{Number(tweaks.glow).toFixed(2)}</span>
          </label>
          <input
            id="tw-glow"
            type="range"
            min="0"
            max="2"
            step="0.05"
            value={tweaks.glow}
            onChange={(event) => updateTweak("glow", Number(event.target.value))}
            style={{ width: "100%", accentColor: "#ff5a8c" }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="tw-tilt"
            style={{ display: "flex", justifyContent: "space-between", fontSize: 11, letterSpacing: "0.06em", color: "rgba(229,255,255,0.7)", marginBottom: 4 }}
          >
            <span>{copy.tweaks.tilt}</span>
            <span>{Number(tweaks.tilt).toFixed(2)}</span>
          </label>
          <input
            id="tw-tilt"
            type="range"
            min="0"
            max="2"
            step="0.05"
            value={tweaks.tilt}
            onChange={(event) => updateTweak("tilt", Number(event.target.value))}
            style={{ width: "100%", accentColor: "#ff5a8c" }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="tw-vig"
            style={{ display: "flex", justifyContent: "space-between", fontSize: 11, letterSpacing: "0.06em", color: "rgba(229,255,255,0.7)", marginBottom: 4 }}
          >
            <span>{copy.tweaks.vignette}</span>
            <span>{Number(tweaks.vignette).toFixed(2)}</span>
          </label>
          <input
            id="tw-vig"
            type="range"
            min="0"
            max="1"
            step="0.02"
            value={tweaks.vignette}
            onChange={(event) => updateTweak("vignette", Number(event.target.value))}
            style={{ width: "100%", accentColor: "#ff5a8c" }}
          />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, color: "rgba(229,255,255,0.85)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={tweaks.showBg}
            onChange={(event) => updateTweak("showBg", event.target.checked)}
            style={{ accentColor: "#ff5a8c" }}
          />
          <span>{copy.tweaks.showBg}</span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, color: "rgba(229,255,255,0.85)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={tweaks.showPanels}
            onChange={(event) => updateTweak("showPanels", event.target.checked)}
            style={{ accentColor: "#ff5a8c" }}
          />
          <span>{copy.tweaks.showPanels}</span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, color: "rgba(229,255,255,0.85)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={tweaks.monochrome}
            onChange={(event) => updateTweak("monochrome", event.target.checked)}
            style={{ accentColor: "#ff5a8c" }}
          />
          <span>{copy.tweaks.monochrome}</span>
        </label>
      </div>
    </div>
  );
};

export default HomePage;
